import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import Input_v2_bbred from "../UI/components/Input_v2_bbred";
import apiRequest from "../service/api/api.request";
import controllerState from "../service/state/controller.state";
import ItemEventLogsController from "../components/logs/ItemEventLogsController";
import appState from "../service/state/app.state";
import ControllersStat from "../components/controllers/ControllersStat";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { Confirm } from "../UI/components/Confirm";
import { FilterLogs } from "../components/logs/FilterLogs";

const POLL_FALLBACK_MS = 30_000;

function usePollIntervalMs() {
  const [intervalMs, setIntervalMs] = useState(() => {
    const sec = Number(appState.time_req_controller);
    if (!Number.isFinite(sec) || sec <= 0) return POLL_FALLBACK_MS;
    return sec * 1000;
  });

  useEffect(
    () =>
      reaction(
        () => appState.time_req_controller,
        (sec) => {
          const n = Number(sec);
          setIntervalMs(
            Number.isFinite(n) && n > 0 ? n * 1000 : POLL_FALLBACK_MS,
          );
        },
      ),
    [],
  );

  return intervalMs;
}

const ControllerIndicatorsSection = observer(
  ({ active, setConfirm, setConfirmMsg }) => {
    if (!active) return null;
    const tc = controllerState.this_controller;
    if (!tc || Array.isArray(tc)) return null;
    return (
      <ControllersStat setConfirm={setConfirm} setConfirmMsg={setConfirmMsg} />
    );
  },
);

const ControllerJournalSection = observer(({ id, active }) => {
  const pollIntervalMs = usePollIntervalMs();

  const readHistoryController = async () => {
    await apiRequest.getHistoryController(id, controllerState.history_page);
    controllerState.tagHistoryFilter();
  };

  const goHistoryPage = (pageNumber) => {
    if (!pageNumber) return;
    controllerState.setParameters("history_page", pageNumber);
  };

  useEffect(() => {
    if (!active) return;

    readHistoryController();

    if (!Number.isFinite(pollIntervalMs) || pollIntervalMs <= 0) return;

    const intervalID = setInterval(() => {
      readHistoryController();
    }, pollIntervalMs);

    return () => clearInterval(intervalID);
  }, [active, id, pollIntervalMs, controllerState.history_page]);

  if (!active) return null;

  return (
    <div className="list_log_controller_card">
      <FilterLogs />
      <div className="log_item">
        <div className="log_item_cell log_item_date_cell">Дата</div>
        <div className="flx_row">
          {controllerState.history_controller
            .filter((t) => t.tag_active)
            .map((tag) => (
              <div className="log_item_cell" key={tag.id_tag ?? tag.name_tag}>
                {tag.name_tag}
              </div>
            ))}
        </div>
      </div>

      {controllerState.history_date.map((date) => (
        <ItemEventLogsController date={date} key={date.date} />
      ))}
      <div className="flx_row" style={{ marginTop: "12px", gap: "12px" }}>
        <span
          className={`red_text nav_controller_item${
            controllerState.history_previous_page
              ? ""
              : " nav_controller_item_disabled"
          }`}
          style={{
            opacity: controllerState.history_previous_page ? 1 : 0.4,
            cursor: controllerState.history_previous_page
              ? "pointer"
              : "default",
          }}
          onClick={() => goHistoryPage(controllerState.history_previous_page)}
        >
          Назад
        </span>
        <span>Страница {controllerState.history_page}</span>
        <span
          className="red_text nav_controller_item"
          style={{
            opacity: controllerState.history_next_page ? 1 : 0.4,
            cursor: controllerState.history_next_page ? "pointer" : "default",
          }}
          onClick={() => goHistoryPage(controllerState.history_next_page)}
        >
          Вперёд
        </span>
      </div>
    </div>
  );
});

const Controller = () => {
  const { id } = useParams();
  const location = useLocation();
  const { name_controller, descriptions, validity_date, identifier } =
    location.state || {};
  const [edit, setEdit] = useState(false);

  const [name, setName] = useState(name_controller);
  const [descriptionsCntrl, setDescriptionsCntrl] = useState(descriptions);
  const [validityDate, setValidityDate] = useState(
    validity_date !== null ? validity_date : "Не задано",
  );
  const [confirm, setConfirm] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [idCntrl, seIdCntrlr] = useState(identifier);
  const [sortFlag, setFlagSort] = useState(false);

  const navigation = useNavigate();
  const [modalConfirm, setModalConfirm] = useState(false);

  const [nav, setNav] = useState([
    {
      name: "Показатель контроллера",
      active: true,
      id: 0,
    },
    {
      name: "Журнал",
      active: false,
      id: 1,
    },
  ]);

  const pollIntervalMs = usePollIntervalMs();
  const refreshInProgress = useRef(false);

  const readController = async () => {
    controllerState.setParameters("controller_id", id);
    await controllerState.createController(id);
  };

  const refreshController = async () => {
    if (refreshInProgress.current) return;
    refreshInProgress.current = true;
    try {
      controllerState.setParameters("controller_id", id);
      await controllerState.refreshControllerData(id);
    } finally {
      refreshInProgress.current = false;
    }
  };

  const moveTagYes = async () => {
    const res = await apiRequest.updateControllerTag(
      controllerState.funcTagMoveData.tag,
      {
        for_categories_controller: controllerState.funcTagMoveData.id
          ? controllerState.funcTagMoveData.id
          : null,
      },
    );
    if (res === 200) {
      setConfirm(false);
    }
  };
  useEffect(() => {
    controllerState.setParameters("history_page", 1);
  }, [id]);

  // Чтение параметров контроллера (полная загрузка + мягкое обновление значений)
  useEffect(() => {
    if (!nav[0].active) return;

    let cancelled = false;
    let intervalID;

    (async () => {
      await readController();
      if (cancelled) return;

      if (!Number.isFinite(pollIntervalMs) || pollIntervalMs <= 0) return;

      intervalID = setInterval(() => {
        if (!cancelled) refreshController();
      }, pollIntervalMs);
    })();

    return () => {
      cancelled = true;
      if (intervalID) clearInterval(intervalID);
    };
  }, [nav[0].active, id, pollIntervalMs]);

  const saveController = async () => {
    const res = await apiRequest.patchController({
      name_controller: name,
      descriptions: descriptionsCntrl,
      id: id,
      identifier: identifier,
    });
    if (res === 200) {
      setEdit(false);
    }
  };

  const removeThisController = async () => {
    try {
      const res = apiRequest.removeController(identifier);
      if (res) {
        setTimeout(() => {
          return navigation("/home");
        }, 300);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const clickNav = (navName) => {
    if (navName.id === 1) {
      controllerState.setParameters("history_page", 1);
    }
    setNav((prevNav) =>
      prevNav.map((item) => ({
        ...item,
        active: item.id === navName.id,
      })),
    );
  };
  // return;
  return (
    <div className="page">
      {modalConfirm ? (
        <Confirm
          title="Вы уверены, что хотите удалить контроллер?"
          no={() => setModalConfirm(false)}
          yes={() => removeThisController()}
          ok="Подтвердить"
          cancel="Отмена"
        />
      ) : (
        <></>
      )}
      {confirm ? (
        <Confirm
          title={confirmMsg}
          no={() => setConfirm(false)}
          yes={moveTagYes}
          ok="Подтвердить"
          cancel="Отмена"
        />
      ) : (
        <></>
      )}
      <ul className="list_profile">
        <li className="flx_bt">
          <span className="list_profile_name ">
            Контроллер:{" "}
            {!edit ? (
              name
            ) : (
              <Input_v2_bbred
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Имя контроллера"
              />
            )}
          </span>
          <div className="controller_action_btn">
            {edit ? (
              <span className="text_btn_no_border" onClick={saveController}>
                Соханить изменения
              </span>
            ) : (
              <></>
            )}

            <span className="text_btn_no_border" onClick={() => setEdit(!edit)}>
              Редактировать контроллер
            </span>
            <span
              className="text_btn_no_border"
              onClick={() => setModalConfirm(true)}
            >
              Удалить контроллер
            </span>
          </div>
        </li>
        <li className="min_h_25">
          <span className="list_profile_span " style={{ width: "135px" }}>
            Идентификатор:{" "}
          </span>

          {idCntrl}
        </li>
        <li className="min_h_25">
          <span className="list_profile_span " style={{ width: "135px" }}>
            Срок действия:{" "}
          </span>
          {!edit ? (
            validityDate
          ) : (
            <Input_v2_bbred
              value={validityDate}
              onChange={(e) => setValidityDate(e.target.value)}
              placeholder="Имя контроллера"
            />
          )}
        </li>
        <li className="min_h_25">
          <span className="list_profile_span " style={{ width: "135px" }}>
            Описание:{" "}
          </span>
          {!edit ? (
            descriptionsCntrl
          ) : (
            <Input_v2_bbred
              value={descriptionsCntrl}
              onChange={(e) => setDescriptionsCntrl(e.target.value)}
              placeholder="Имя контроллера"
            />
          )}
        </li>
      </ul>

      <div className="nav_controller_container">
        {nav.map((n) => (
          <span
            onClick={() => clickNav(n)}
            className="red_text nav_controller_item"
          >
            {n?.name}
          </span>
        ))}
      </div>
      <ControllerIndicatorsSection
        active={nav[0].active}
        setConfirm={setConfirm}
        setConfirmMsg={setConfirmMsg}
      />
      <ControllerJournalSection id={id} active={nav[1].active} />
    </div>
  );
};

export default Controller;
